import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Menu, X, LogOut } from "lucide-react";
import { useAuth } from "@/auth/AuthProvider";

const NAV_LINKS = [
  { label: "How it works", href: "/#how-it-works", anchor: true },
  { label: "Pricing", href: "/pricing", anchor: false },
  { label: "Why GushPay", href: "/#proof", anchor: true },
];

export default function Navbar({ onDemoClick }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleLogin = () => {
    setOpen(false);
    navigate("/login");
  };

  const handleDashboard = () => {
    setOpen(false);
    navigate("/dashboard");
  };

  const handleLogout = async () => {
    setOpen(false);
    await logout();
  };

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-40 transition-all duration-300 ${
        scrolled
          ? "bg-[#F4F3EE]/85 backdrop-blur-xl border-b border-[#D3D9DE]"
          : "bg-transparent"
      }`}
    >
      <div className="gp-container flex h-16 md:h-20 items-center justify-between">
        <a
          href="#top"
          data-testid="nav-logo"
          className="flex items-center gap-2 group"
          onClick={(e) => {
            e.preventDefault();
            window.scrollTo({ top: 0, behavior: "smooth" });
          }}
        >
          <span
            aria-hidden
            className="inline-block h-2.5 w-2.5 bg-[#D67229] group-hover:bg-[#0F4C5C] transition-colors duration-300"
          />
          <span className="font-display text-2xl font-bold tracking-tight text-[#111418]">
            GushPay
          </span>
        </a>

        <nav className="hidden md:flex items-center gap-10">
          {NAV_LINKS.map((link) =>
            link.anchor ? (
              <a
                key={link.label}
                href={link.href}
                data-testid={`nav-link-${link.label.toLowerCase().replace(/\s/g, "-")}`}
                className="font-mono text-[11px] uppercase tracking-[0.22em] text-[#2C333A] hover:text-[#D67229] transition-colors"
              >
                {link.label}
              </a>
            ) : (
              <Link
                key={link.label}
                to={link.href}
                data-testid={`nav-link-${link.label.toLowerCase().replace(/\s/g, "-")}`}
                className="font-mono text-[11px] uppercase tracking-[0.22em] text-[#2C333A] hover:text-[#D67229] transition-colors"
              >
                {link.label}
              </Link>
            )
          )}
        </nav>

        <div className="flex items-center gap-3">
          {user ? (
            <>
              <button
                type="button"
                onClick={handleDashboard}
                data-testid="nav-dashboard-button"
                className="gp-btn gp-btn-primary hidden sm:inline-flex"
              >
                Open Dashboard
              </button>
              <button
                type="button"
                onClick={handleLogout}
                aria-label="Logout"
                data-testid="nav-logout-button"
                className="hidden sm:inline-flex items-center justify-center h-10 w-10 border border-[#111418] text-[#111418] hover:bg-[#111418] hover:text-[#F4F3EE] transition-colors"
              >
                <LogOut size={16} />
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={handleLogin}
                data-testid="nav-login-button"
                className="hidden md:inline-flex font-mono text-[11px] uppercase tracking-[0.22em] text-[#0F4C5C] hover:text-[#D67229] transition-colors"
              >
                Sign in
              </button>
              <Link
                to="/pricing"
                data-testid="nav-cta-button"
                className="gp-btn gp-btn-primary hidden sm:inline-flex"
              >
                Get Started
              </Link>
            </>
          )}
          <button
            type="button"
            aria-label="Toggle navigation"
            data-testid="nav-mobile-toggle"
            className="md:hidden inline-flex items-center justify-center h-10 w-10 border border-[#111418] text-[#111418]"
            onClick={() => setOpen((v) => !v)}
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden bg-[#F4F3EE] border-t border-[#D3D9DE]" data-testid="nav-mobile-panel">
          <div className="gp-container py-6 flex flex-col gap-5">
            {NAV_LINKS.map((link) =>
              link.anchor ? (
                <a
                  key={link.label}
                  href={link.href}
                  onClick={() => setOpen(false)}
                  className="font-mono text-xs uppercase tracking-[0.22em] text-[#2C333A]"
                >
                  {link.label}
                </a>
              ) : (
                <Link
                  key={link.label}
                  to={link.href}
                  onClick={() => setOpen(false)}
                  className="font-mono text-xs uppercase tracking-[0.22em] text-[#2C333A]"
                >
                  {link.label}
                </Link>
              )
            )}
            {user ? (
              <>
                <button
                  type="button"
                  onClick={handleDashboard}
                  data-testid="nav-dashboard-button-mobile"
                  className="gp-btn gp-btn-primary self-start"
                >
                  Open Dashboard
                </button>
                <button
                  type="button"
                  onClick={handleLogout}
                  data-testid="nav-logout-button-mobile"
                  className="gp-btn gp-btn-ghost self-start"
                >
                  <LogOut size={14} /> Logout
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  onClick={handleLogin}
                  data-testid="nav-login-button-mobile"
                  className="gp-btn gp-btn-ghost self-start"
                >
                  Sign in
                </button>
                <Link
                  to="/pricing"
                  onClick={() => setOpen(false)}
                  data-testid="nav-cta-button-mobile"
                  className="gp-btn gp-btn-primary self-start"
                >
                  Get Started
                </Link>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
